import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Share2, Heart, TrendingDown, TrendingUp, Database, CheckCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../store/authStore';
import { getSession, getReadings } from '../../api/sessions';
import { SkeletonBlock } from '../../components/SkeletonBlock';
type FilterRange = 'full' | 'firstHour' | 'lastHour' | 'custom';
export function SessionDetail() {
  const {
    sessionId
  } = useParams();
  const navigate = useNavigate();
  const {
    token
  } = useAuth();
  const [session, setSession] = useState<any>(null);
  const [allReadings, setAllReadings] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<FilterRange>('full');
  useEffect(() => {
    if (!token || !sessionId) return;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [sessionRes, readingsRes] = await Promise.all([getSession(token, sessionId), getReadings(token, sessionId, {
          limit: 2000
        })]);
        setSession(sessionRes);
        setAllReadings(readingsRes.readings || []);
      } catch (err) {
        // console.error('Failed to fetch session detail', err)
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token, sessionId]);
  // Filter Readings
  const filteredReadings = useMemo(() => {
    if (filter === 'full' || allReadings.length === 0) return allReadings;
    const sorted = [...allReadings].sort((a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime());
    const firstTime = new Date(sorted[0].recordedAt).getTime();
    const lastTime = new Date(sorted[sorted.length - 1].recordedAt).getTime();
    if (filter === 'firstHour') {
      const oneHourLater = firstTime + 60 * 60 * 1000;
      return sorted.filter(r => new Date(r.recordedAt).getTime() <= oneHourLater);
    }
    if (filter === 'lastHour') {
      const oneHourBefore = lastTime - 60 * 60 * 1000;
      return sorted.filter(r => new Date(r.recordedAt).getTime() >= oneHourBefore);
    }
    return allReadings;
  }, [allReadings, filter]);
  // Split by sensor type
  const bpmReadings = useMemo(() => filteredReadings.filter(r => r.sensorType === 'bpm'), [filteredReadings]);
  const piezoReadings = useMemo(() => filteredReadings.filter(r => r.sensorType === 'sensor2'), [filteredReadings]);
  // Compute Metrics
  const metrics = useMemo(() => {
    if (bpmReadings.length === 0) return {
      avg: 0,
      min: 0,
      max: 0,
      minTime: '',
      maxTime: ''
    };
    let sum = 0;
    let min = Infinity;
    let max = -Infinity;
    let minTime = '';
    let maxTime = '';
    bpmReadings.forEach(r => {
      const val = r.readingValue;
      sum += val;
      if (val < min) {
        min = val;
        minTime = r.recordedAt;
      }
      if (val > max) {
        max = val;
        maxTime = r.recordedAt;
      }
    });
    return {
      avg: Math.round(sum / bpmReadings.length),
      min,
      max,
      minTime: new Date(minTime).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      }),
      maxTime: new Date(maxTime).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })
    };
  }, [bpmReadings]);
  // Compute Notable Events
  const events = useMemo(() => {
    const result: any[] = [];
    if (bpmReadings.length === 0) return result;
    let currentState = 'normal'; // 'normal', 'high', 'low'
    const sorted = [...bpmReadings].sort((a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime());
    sorted.forEach(r => {
      const val = r.readingValue;
      const time = new Date(r.recordedAt).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      });
      if (val > 160 && currentState !== 'high') {
        result.push({
          type: 'high',
          value: val,
          time,
          desc: `BPM reached ${val} (above normal)`
        });
        currentState = 'high';
      } else if (val < 110 && currentState !== 'low') {
        result.push({
          type: 'low',
          value: val,
          time,
          desc: `BPM dropped to ${val} (below normal)`
        });
        currentState = 'low';
      } else if (val >= 110 && val <= 160 && currentState !== 'normal') {
        result.push({
          type: 'normal',
          value: val,
          time,
          desc: `BPM returned to normal range (${val})`
        });
        currentState = 'normal';
      }
    });
    return result;
  }, [bpmReadings]);
  // Formatters
  const formatTimeTick = (tickItem: any) => {
    return new Date(tickItem).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  const CustomTooltip = ({
    active,
    payload,
    label,
    borderColor
  }: any) => {
    if (active && payload && payload.length) {
      return <div style={{
        background: 'white',
        border: 'none',
        borderRadius: '10px',
        boxShadow: '0 4px 16px rgba(107,33,232,0.2)',
        padding: '8px 12px',
        borderTop: `3px solid ${borderColor}`
      }} data-id="element-994" data-mp="ataasoyuckp4">
          <p className="text-[12px] text-text-secondary mb-1" data-id="element-995" data-mp="5txhbldgsfdk">
            {formatTimeTick(label)}
          </p>
          <p className="text-[16px] font-bold text-text-primary" data-id="element-996" data-mp="3o02d2iwxjmgt">
            {payload[0].value}{' '}
            <span className="text-[12px] font-normal" data-id="element-997" data-mp="3sz6iqzknb22e">val</span>
          </p>
        </div>;
    }
    return null;
  };
  const getStatusPill = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="bg-danger/15 text-danger px-3 py-1 rounded-pill text-[13px] font-medium" data-id="element-998" data-mp="1r4o360aspuu5">
            Active
          </span>;
      case 'incomplete':
        return <span className="bg-amber-500/15 text-amber-600 px-3 py-1 rounded-pill text-[13px] font-medium" data-id="element-999" data-mp="1kp73dfj7k3eh">
            Incomplete
          </span>;
      default:
        return <span className="bg-accent/20 text-accent px-3 py-1 rounded-pill text-[13px] font-medium" data-id="element-1000" data-mp="1atvn2ss5io4m">
            Completed
          </span>;
    }
  };
  const getBpmStatusLabel = (bpm: number) => {
    if (bpm < 110) return <span className="bg-amber-500/15 text-amber-600 px-2 py-0.5 rounded-pill text-[11px] font-medium" data-id="element-1001" data-mp="1yq9s601l7rt3">
          Low
        </span>;
    if (bpm > 160) return <span className="bg-danger/15 text-danger px-2 py-0.5 rounded-pill text-[11px] font-medium" data-id="element-1002" data-mp="1tqwwqqwnhcew">
          High
        </span>;
    return <span className="bg-accent/20 text-accent px-2 py-0.5 rounded-pill text-[11px] font-medium" data-id="element-1003" data-mp="2mflyms5j5lrp">
        Normal
      </span>;
  };
  const handleShare = () => {
    navigate(`/app/history/${sessionId}/share`, {
      state: {
        avgBpm: metrics.avg,
        minBpm: metrics.min,
        maxBpm: metrics.max,
        totalReadings: filteredReadings.length,
        sessionId
      }
    });
  };
  return <main className="w-full h-full flex flex-col pb-8" data-id="element-1004" data-mp="38pasfxymoodv">
      {/* Header */}
      <div className="flex items-center justify-between mb-6" data-id="element-1005" data-mp="3dnvy4cwng09p">
        <div className="flex items-center" data-id="element-1006" data-mp="3dljgy1ypttxp">
          <button onClick={() => navigate('/app/history')} className="mr-4 text-text-secondary hover:text-text-primary transition-colors" aria-label="Go back" data-id="element-1007" data-mp="3ho607ehbm6ja">
            <ArrowLeft size={24} data-id="element-1008" data-mp="3rdplsrwf4ehu" />
          </button>
          <h1 className="text-[20px] font-semibold text-text-primary" data-id="element-1009" data-mp="3rglmgnznhh9z">
            Session Detail
          </h1>
        </div>
        <button onClick={handleShare} disabled={isLoading} className="flex items-center space-x-2 border border-primary text-primary px-4 py-2 rounded-pill text-[14px] font-medium hover:bg-primary/5 transition-colors disabled:opacity-50" data-id="element-1010" data-mp="57147mxtxq7r">
          <Share2 size={16} data-id="element-1011" data-mp="zj764tycrawz" />
          <span data-id="element-1012" data-mp="zjfnay1id2dz">Share</span>
        </button>
      </div>

      <div className="flex flex-col xl:grid xl:grid-cols-[1fr_380px] xl:gap-8" data-id="element-1013" data-mp="zgtdssv24ct4">
        {/* LEFT COLUMN (Charts & Timeline) */}
        <div className="flex flex-col space-y-6 order-2 xl:order-1 mt-6 xl:mt-0" data-id="element-1014" data-mp="6u0co9dr9bil">
          {/* BPM Chart */}
          <div className="bg-white rounded-[20px] shadow-sm p-4 md:p-5" data-id="element-1015" data-mp="2tpw62sc72ph">
            <div className="flex justify-between items-center mb-2" data-id="element-1016" data-mp="gk1nw0l0ezg3">
              <h2 className="text-[16px] font-semibold text-text-primary" data-id="element-1017" data-mp="gmeef24u2i4r">
                Fetal Heart Rate
              </h2>
              <span className="text-[14px] text-text-secondary" data-id="element-1018" data-mp="3k94zt4eqoc3i">
                {filter === 'full' ? 'Full session' : filter === 'firstHour' ? 'First hour' : 'Last hour'}
              </span>
            </div>
            <div className="flex items-center mb-6" data-id="element-1019" data-mp="1uqg69n46700m">
              <div className="w-[20px] h-[2px] bg-primary rounded-[1px] mr-2" data-id="element-1020" data-mp="1zjsw20kl5cuy" />
              <span className="text-[12px] text-text-secondary" data-id="element-1021" data-mp="1lsdzss1o3f5y">BPM</span>
            </div>

            {isLoading ? <SkeletonBlock height="240px" data-id="element-1022" data-mp="1qrh7ylcy21i5" /> : <div className="h-[240px] md:h-[300px] w-full" data-id="element-1023" data-mp="2efqmr3bqpozs">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1024" data-mp="2ee5q985v7vza">
                  <LineChart data={bpmReadings} margin={{
                top: 5,
                right: 5,
                left: -20,
                bottom: 5
              }} data-id="element-1025" data-mp="2igkb1xt3h5t7">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E9D5FF" vertical={false} data-id="element-1026" data-mp="t2e4nkwyxgga" />
                    <XAxis dataKey="recordedAt" tickFormatter={formatTimeTick} stroke="#8A8A8A" fontSize={11} tickLine={false} axisLine={false} minTickGap={30} data-id="element-1027" data-mp="7apdet1a2z18" />
                    <YAxis domain={[80, 180]} stroke="#8A8A8A" fontSize={11} tickLine={false} axisLine={false} data-id="element-1028" data-mp="2j7gcg2dlilj" />
                    <Tooltip content={<CustomTooltip borderColor="#6B21E8" data-id="element-1030" data-mp="3arxtb7cg3otb" />} data-id="element-1029" data-mp="3op2uou17rzi8" />
                    <Line type="monotone" dataKey="readingValue" stroke="#6B21E8" strokeWidth={2} dot={false} activeDot={{
                  r: 4,
                  fill: '#6B21E8',
                  strokeWidth: 0
                }} isAnimationActive={true} data-id="element-1031" data-mp="3fqhj5iu5tkqy" />
                  </LineChart>
                </ResponsiveContainer>
              </div>}
          </div>

          {/* Uterine Activity Chart */}
          <div className="bg-white rounded-[20px] shadow-sm p-4 md:p-5" data-id="element-1032" data-mp="33fq78x3zfdet">
            <div className="flex justify-between items-center mb-2" data-id="element-1033" data-mp="33ef5t3y0ehqv">
              <h2 className="text-[16px] font-semibold text-text-primary" data-id="element-1034" data-mp="3h63gd2sm0y5q">
                Uterine Activity
              </h2>
            </div>
            <div className="flex items-center mb-6" data-id="element-1035" data-mp="3r5kg73l6khts">
              <div className="w-[20px] h-[2px] bg-accent rounded-[1px] mr-2" data-id="element-1036" data-mp="3r5c6tlo9nunc" />
              <span className="text-[12px] text-text-secondary" data-id="element-1037" data-mp="3m7soa9kjcvpl">Sensor 2</span>
            </div>

            {isLoading ? <SkeletonBlock height="240px" data-id="element-1038" data-mp="1ws32e8akuzby" /> : <div className="h-[240px] md:h-[300px] w-full" data-id="element-1039" data-mp="1rtrtq0w0quvb">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1040" data-mp="2hgvmhwns40bj">
                  <LineChart data={piezoReadings} margin={{
                top: 5,
                right: 5,
                left: -20,
                bottom: 5
              }} data-id="element-1041" data-mp="2hiotxe67uokl">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E9D5FF" vertical={false} data-id="element-1042" data-mp="2vhofue2cgpd2" />
                    <XAxis dataKey="recordedAt" tickFormatter={formatTimeTick} stroke="#8A8A8A" fontSize={11} tickLine={false} axisLine={false} minTickGap={30} data-id="element-1043" data-mp="34gtdli630eo6" />
                    <YAxis domain={[0, 4095]} stroke="#8A8A8A" fontSize={11} tickLine={false} axisLine={false} data-id="element-1044" data-mp="3j7sk8nncwt54" />
                    <Tooltip content={<CustomTooltip borderColor="#00D4D4" data-id="element-1046" data-mp="17huflfw8g5hs" />} data-id="element-1045" data-mp="17ivujvgddc4s" />
                    <Line type="monotone" dataKey="readingValue" stroke="#00D4D4" strokeWidth={2} dot={false} activeDot={{
                  r: 4,
                  fill: '#00D4D4',
                  strokeWidth: 0
                }} isAnimationActive={true} data-id="element-1047" data-mp="12izozrs0xcn4" />
                  </LineChart>
                </ResponsiveContainer>
              </div>}
          </div>

          {/* Timeline */}
          <div className="pt-4" data-id="element-1048" data-mp="2dz7uo22r5430">
            <h2 className="text-[18px] font-semibold text-text-primary mb-4" data-id="element-1049" data-mp="2e0ixnzodi7t2">
              Notable Events
            </h2>

            {isLoading ? <SkeletonBlock height="100px" data-id="element-1050" data-mp="1tpov6ibuoy2i" /> : events.length === 0 ? <div className="bg-accent/10 border border-accent/20 rounded-[16px] p-4 flex items-start space-x-3" data-id="element-1051" data-mp="1ypb44fknbs5i">
                <CheckCircle size={20} className="text-accent flex-shrink-0 mt-0.5" data-id="element-1052" data-mp="1jsk361vbq8ds" />
                <p className="text-[14px] text-text-primary" data-id="element-1053" data-mp="1orna5ruv6ooe">
                  No notable events — heart rate remained stable throughout this
                  session.
                </p>
              </div> : <div className="relative pl-4" data-id="element-1054" data-mp="2hhn8t8yrv5it">
                <div className="absolute left-[21px] top-2 bottom-2 w-[1px] bg-border" data-id="element-1055" data-mp="22pv4ds0inbvv" />
                <div className="space-y-6" data-id="element-1056" data-mp="27p7q29fnicwv">
                  {events.map((ev, i) => <div key={i} className="relative flex items-start" data-id="element-1057" data-mp="2vf224os5bxg2" data-mpi={i}>
                      <div className={`absolute left-0 w-[12px] h-[12px] rounded-full mt-1 z-10 ${ev.type === 'high' ? 'bg-danger' : ev.type === 'low' ? 'bg-amber-500' : 'bg-accent'}`} data-id="element-1058" data-mp="2vdqx6lmysjnd" />
                      <div className="ml-8" data-id="element-1059" data-mp="jt070gxvzhmv">
                        <span className="text-[13px] font-semibold text-text-primary block" data-id="element-1060" data-mp="jrykngbf78pu">
                          {ev.time}
                        </span>
                        <span className="text-[13px] text-text-secondary" data-id="element-1061" data-mp="thrx0jjdzrv5">
                          {ev.desc}
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
        </div>

        {/* RIGHT COLUMN (Info & Metrics) */}
        <div className="flex flex-col space-y-6 order-1 xl:order-2" data-id="element-1062" data-mp="er11p1il6ana">
          {/* Session Info */}
          <div className="bg-white rounded-[20px] shadow-sm p-5" data-id="element-1063" data-mp="atmv7hmaigyz">
            {isLoading ? <SkeletonBlock height="120px" data-id="element-1064" data-mp="1qadkiyorc85z" /> : <>
                <div className="mb-4" data-id="element-1065" data-mp="1m65ripb9o61y">
                  <p className="text-[15px] font-semibold text-text-primary" data-id="element-1066" data-mp="ivryk7z0mukr">
                    {new Date(session?.startedAt || Date.now()).toLocaleDateString('en-US', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
                  </p>
                  <p className="text-[14px] text-text-secondary mt-1" data-id="element-1067" data-mp="5ts1wav99j8">
                    {new Date(session?.startedAt || Date.now()).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit'
                })}{' '}
                    —{' '}
                    {session?.endedAt ? new Date(session.endedAt).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit'
                }) : 'Ongoing'}
                  </p>
                </div>
                <div className="mb-4" data-id="element-1068" data-mp="3a06gfrlz4419">
                  <p className="text-[14px] text-text-secondary" data-id="element-1069" data-mp="352o55ukhhcq3">Duration</p>
                  <p className="text-[15px] font-medium text-text-primary" data-id="element-1070" data-mp="351cxvkrtxmu8">
                    {session?.duration ? `${Math.floor(session.duration / 60)} hours ${session.duration % 60} minutes` : '--'}
                  </p>
                </div>
                <div data-id="element-1071" data-mp="2r59b2cjacgrs">
                  {getStatusPill(session?.sessionStatus || 'completed')}
                </div>
              </>}
          </div>

          {/* Filter Bar */}
          <div className="flex overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 hide-scrollbar space-x-2" data-id="element-1072" data-mp="2hbiqhiqa4qhd">
            {[{
            id: 'full',
            label: 'Full session'
          }, {
            id: 'firstHour',
            label: 'First hour'
          }, {
            id: 'lastHour',
            label: 'Last hour'
          }].map(f => <button key={f.id} onClick={() => setFilter(f.id as FilterRange)} className={`flex-shrink-0 h-[32px] px-4 rounded-pill text-[13px] font-medium transition-colors duration-200 ${filter === f.id ? 'bg-primary text-white' : 'bg-border text-text-secondary hover:bg-border/80'}`} data-id="element-1073" data-mp="4gyrtellrae">
                {f.label}
              </button>)}
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-2 gap-4" data-id="element-1074" data-mp="2zjcctue533az">
            {/* Avg BPM */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-1075" data-mp="33ko4s6khk0mc">
              <div className="w-[32px] h-[32px] bg-primary/10 rounded-full flex items-center justify-center mb-3" data-id="element-1076" data-mp="33il32p9z0j1s">
                <Heart size={16} className="text-primary" data-id="element-1077" data-mp="2ujzrxf6tw6b1" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-1078" data-mp="2axxwm33q9bpx" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-1079" data-mp="25zwd5h2e3adx">
                  {metrics.avg || '--'}
                </span>}
              <span className="text-[12px] text-text-secondary mb-2" data-id="element-1080" data-mp="2kuaocvfppfi1">
                Average BPM
              </span>
              {!isLoading && metrics.avg > 0 && getBpmStatusLabel(metrics.avg)}
            </div>

            {/* Min BPM */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-1081" data-mp="2oz0gc97929xo">
              <div className="w-[32px] h-[32px] bg-amber-500/15 rounded-full flex items-center justify-center mb-3" data-id="element-1082" data-mp="33nobwfh3ijnm">
                <TrendingDown size={16} className="text-amber-600" data-id="element-1083" data-mp="2yp39c9edfnw0" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-1084" data-mp="3cocpj92kuhzz" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-1085" data-mp="38nj54pocflbu">
                  {metrics.min === Infinity ? '--' : metrics.min}
                </span>}
              <span className="text-[12px] text-text-secondary mb-1" data-id="element-1086" data-mp="3g53a8q7yysh8">
                Minimum BPM
              </span>
              <span className="text-[11px] text-text-secondary" data-id="element-1087" data-mp="3u4utb5dcez23">
                at {metrics.minTime || '--'}
              </span>
            </div>

            {/* Max BPM */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-1088" data-mp="31hs1ew369mkt">
              <div className="w-[32px] h-[32px] bg-accent/20 rounded-full flex items-center justify-center mb-3" data-id="element-1089" data-mp="2xg6hiqs80814">
                <TrendingUp size={16} className="text-accent" data-id="element-1090" data-mp="3b7jvlp7d0dor" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-1091" data-mp="36eqs14nmnbe9" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-1092" data-mp="2iowgqriyhuw4">
                  {metrics.max === -Infinity ? '--' : metrics.max}
                </span>}
              <span className="text-[12px] text-text-secondary mb-1" data-id="element-1093" data-mp="t7sl99twujvu">
                Maximum BPM
              </span>
              <span className="text-[11px] text-text-secondary" data-id="element-1094" data-mp="y26jdjzyj547">
                at {metrics.maxTime || '--'}
              </span>
            </div>

            {/* Total Readings */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-1095" data-mp="p8078hlqzlj3">
              <div className="w-[32px] h-[32px] bg-primary/10 rounded-full flex items-center justify-center mb-3" data-id="element-1096" data-mp="1cynr818xu1m0">
                <Database size={16} className="text-primary" data-id="element-1097" data-mp="1gy4z11bu7auc" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-1098" data-mp="1319by4yr3iva" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-1099" data-mp="180cjpx64836y">
                  {filteredReadings.length}
                </span>}
              <span className="text-[12px] text-text-secondary mb-1" data-id="element-1100" data-mp="3spa3r1l1pvaf">
                Data points
              </span>
              <span className="text-[11px] text-text-secondary" data-id="element-1101" data-mp="38lkylsnnbnql">
                {!isLoading && session?.duration ? `~${(filteredReadings.length / session.duration).toFixed(1)} per min` : '--'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <style data-id="element-1102" data-mp="2usv5735sfgjg">{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </main>;
}